// ═══════════════════════════════════════════════════════════════════
// FINANCIAL MODEL — ported verbatim from `financial dashboard ipad.html`.
//
// The arithmetic in this file is the specification. Every expression,
// every default fallback and every order of operation is equivalent to
// the original. It is pinned by test/regression.test.mjs, which executes
// the ORIGINAL code out of the HTML file and compares, to the penny,
// against this module.
//
// Do not "tidy" the maths here. If a number needs to change, change it
// deliberately and re-run the regression test in the same change.
// ═══════════════════════════════════════════════════════════════════

export const DEFAULTS = {
  asOfDate: 'May 2026', startYear: 2026, startMonth: 5,
  jamesPB: 50000, joannePB: 50000, jamesISA: 43738, joanneISA: 43738,
  jamesCashISA: 15568, joanneCashISA: 0,
  jamesPension: 216725, joannePension: 130371,
  company: 51095, houseValue: 290000, mortgageRemaining: 2910,
  mortgageCleared: false, mortgageClearYear: 2026, mortgageClearMonth: 10,
  inheritancePending: true, inheritanceAmount: 33000,
  inheritanceYear: 2026, inheritanceMonth: 12,
  isaCurrentEach: 500, isaPostMortgageEach: 1000, pensionEach: 500,
  joanneTopUpAmount: 20000, postParityTopUp: 10000,
  isaGrowthPct: 8.0, jamesPensionGrowthPct: 5.5, joannePensionGrowthPct: 4.0,
  pbPrizeRatePct: 4.4, drawdownGrowthPct: 4.0, inflationPct: 2.5, cashISAGrowthPct: 3.6,
  statePension2026: 11502, jamesStatePensionYear: 2039,
  joanneStatePensionYear: 2041, personalAllowance: 12570,
};

// ═══════════════════════════════════════════
// SIMULATION — 420 monthly steps (35 years)
// ═══════════════════════════════════════════
export function simulate(s) {
  let jPB = s.jamesPB || 0, oPB = s.joannePB || 0, jISA = s.jamesISA || 0, oISA = s.joanneISA || 0;
  let jPen = s.jamesPension || 0, oPen = s.joannePension || 0;
  let jCash = s.jamesCashISA || 0, oCash = s.joanneCashISA || 0;
  const rISA = (s.isaGrowthPct || 8) / 100 / 12, rJP = (s.jamesPensionGrowthPct || 5.5) / 100 / 12;
  const rOP = (s.joannePensionGrowthPct || 4) / 100 / 12, rPB = (s.pbPrizeRatePct || 4.4) / 100 / 12;
  const rCash = (s.cashISAGrowthPct || 3.6) / 100 / 12;
  const cNet = (s.company || 0) * 0.9, sy = s.startYear || 2026, sm = s.startMonth || 5;
  let inhDone = !s.inheritancePending, parityDone = false;
  const res = [];
  for (let i = 0; i < 420; i++) {
    const mo = i + (sm - 1), year = sy + Math.floor(mo / 12), month = (mo % 12) + 1;

    // 1. Growth applied before contributions
    jISA *= (1 + rISA); oISA *= (1 + rISA); jPen *= (1 + rJP); oPen *= (1 + rOP);
    jCash *= (1 + rCash); oCash *= (1 + rCash);

    // 2. Premium Bond prizes: earned on the combined holding, but they refill
    //    James's pot to the 50k cap first; only the overflow reaches the ISAs.
    //    (Joanne's PB balance is therefore static - this is the original behaviour.)
    const pr = (jPB + oPB) * rPB;
    if (jPB < 50000) {
      const t = Math.min(pr, 50000 - jPB); jPB = Math.min(50000, jPB + t);
      jISA += (pr - t) / 2; oISA += (pr - t) / 2;
    } else { jISA += pr / 2; oISA += pr / 2; }

    // 3. Contributions - ISA rate steps up once the mortgage is gone
    const pm = s.mortgageCleared || year > (s.mortgageClearYear || 2026) ||
      (year === (s.mortgageClearYear || 2026) && month >= (s.mortgageClearMonth || 10));
    const isaC = pm ? (s.isaPostMortgageEach || 1000) : (s.isaCurrentEach || 500);
    jISA += isaC; oISA += isaC; jPen += (s.pensionEach || 500); oPen += (s.pensionEach || 500);

    // 4. Inheritance: tops James's PBs back to 50k, remainder to cash ISAs
    //    (cash ISAs are EXCLUDED from all wealth/retirement calcs)
    if (!inhDone && year === (s.inheritanceYear || 2026) && month === (s.inheritanceMonth || 12)) {
      inhDone = true; let r = s.inheritanceAmount || 0;
      const t = Math.max(0, 50000 - jPB); jPB = 50000; r -= t;
      jCash += r / 2; oCash += r / 2;
    }

    // 5. April pension top-ups: Joanne alone until she reaches parity, then both
    if (month === 4 && year >= 2027) {
      if (!parityDone) { oPen += (s.joanneTopUpAmount || 20000); if (oPen >= jPen) parityDone = true; }
      else { jPen += (s.postParityTopUp || 10000); oPen += (s.postParityTopUp || 10000); }
    }

    // NB: total deliberately EXCLUDES cash ISA (jCash/oCash) per plan
    res.push({
      year, month, i, jPB, oPB, jISA, oISA, jPen, oPen, jCash, oCash, cNet,
      total: jPB + oPB + jISA + oISA + jPen + oPen + cNet,
    });
  }
  return res;
}

// One row per calendar year: the December entry where a full year exists,
// otherwise the first month seen for that year.
export function annualSnaps(m) {
  const map = {};
  for (const d of m) { if (!map[d.year] || d.month === 12) map[d.year] = d; }
  return Object.values(map).sort((a, b) => a.year - b.year);
}

// ═══════════════════════════════════════════
// RETIREMENT / DECUMULATION
// ═══════════════════════════════════════════

// State pension net of basic-rate tax on the slice above the personal allowance
export function netSP(sp, pa) { if (sp <= 0) return 0; return sp - Math.max(0, sp - pa) * 0.20; }

// Unified decumulation: tax-sensible split between pension and tax-free savings
// (ISA + Premium Bonds + company reserve).
// Sequence each year, to meet the net spending need after state pension:
//   1) Draw pension up to each person's unused personal allowance (0% tax).
//   2) Top up from tax-free savings (Premium Bonds first conceptually, then ISA).
//   3) Only if savings are exhausted, draw extra pension taxed at 20%.
// Returns { viable, data[] } where data is in thousands.
export function runDrawdown(snap, ry, incR, da, s) {
  const end = 1974 + da, infl = (s.inflationPct || 2.5) / 100, dr = (s.drawdownGrowthPct || 4) / 100;
  const pa = s.personalAllowance || 12570, jSPY = s.jamesStatePensionYear || 2039,
    oSPY = s.joanneStatePensionYear || 2041, sp0 = s.statePension2026 || 11502;
  let pension = (snap.jPen || 0) + (snap.oPen || 0);
  // company reserve wound up at retirement
  let savings = (snap.jISA || 0) + (snap.oISA || 0) + (snap.jPB || 0) + (snap.oPB || 0) + (snap.cNet || 0);
  const data = []; let viable = true;
  for (let y = ry; y <= end; y++) {
    pension *= (1 + dr); savings *= (1 + dr);
    const jSP = y >= jSPY ? sp0 * Math.pow(1 + infl, y - 2026) : 0,
      oSP = y >= oSPY ? sp0 * Math.pow(1 + infl, y - 2026) : 0;
    const targetNom = incR * Math.pow(1 + infl, y - 2026);
    let need = Math.max(0, targetNom - netSP(jSP, pa) - netSP(oSP, pa));

    // 1) free pension (fills remaining personal allowance at 0% tax)
    const freeCap = Math.max(0, pa - jSP) + Math.max(0, pa - oSP);
    const penFree = Math.min(need, freeCap, pension); pension -= penFree; need -= penFree;

    // 2) tax-free savings
    const savDraw = Math.min(need, savings); savings -= savDraw; need -= savDraw;

    // 3) extra pension taxed at 20%
    let penTaxed = 0;
    if (need > 0 && pension > 0) {
      let gross = Math.min(need / 0.8, pension); pension -= gross; penTaxed = gross; need -= gross * 0.8;
    }
    if (need > 1) viable = false; // couldn't meet target this year

    data.push({
      year: y, pen: (penFree + penTaxed) / 1000, sav: savDraw / 1000,
      jSP: jSP / 1000, oSP: oSP / 1000,
      pensionPot: Math.max(0, pension) / 1000, savingsPot: Math.max(0, savings) / 1000,
    });
  }
  return { viable, data };
}

export function isViable(snap, ry, incR, da, s) { return runDrawdown(snap, ry, incR, da, s).viable; }

// Earliest year whose portfolio can sustain the target through to `da`
export function findRY(annual, incR, da, s) {
  const min = Math.max(2031, s.startYear || 2026);
  for (let y = min; y <= 2048; y++) {
    const sn = annual.find(d => d.year === y);
    if (sn && isViable(sn, y, incR, da, s)) return y;
  }
  return 2049; // sentinel: "not within the modelled horizon"
}

export function buildPR(snap, ry, incR, da, s) { return runDrawdown(snap, ry, incR, da, s).data; }

// ═══════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════
export const INC = [36000, 40000, 45400, 50000, 55000, 60000, 62700];
// PLSA two-person Retirement Living Standards
export const INC_LABEL = { 45400: 'Moderate', 62700: 'Comfortable' };
export const DAS = [75, 80, 85, 90, 95, 100];
export const MO = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Build the full income x death-age grid of earliest retirement years
export function buildRetGrid(annual, s) {
  const g = {};
  for (const i of INC) { g[i] = {}; for (const d of DAS) g[i][d] = findRY(annual, i, d, s); }
  return g;
}
